import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useGetDraftQuery, useUpdateImplementerReviewMutation, useCreateDraftCheckoutLogMutation, useUpdateDraftMutation } from '../../api/implementerInboxSlice';
import { useLogUserActionMutation } from '../../api/apiSlice';
import { useParams, useNavigate } from 'react-router';
import { Stack } from '@mui/material';
import ImplementerReviewCardHeader from './ImplementerReviewCardHeader';
import StaffReviewChangesOnlyView from '../StaffReviewPortal/v2/StaffReviewChangesOnlyView';
import AllItemsView from '../StaffReviewPortal/v2/AllItemsView';
import { differenceInMinutes } from 'date-fns';
import { UserContext } from '../Hooks/useUser';
import SubmitReviewConfirmation from '../StaffReviewPortal/v2/SubmitReviewConfirmation';
import CheckedOutCardDialog from '../StaffReviewPortal/CheckedOutCardDialog';
import CheckoutTimeoutDialog from '../StaffReviewPortal/CheckoutTimeoutDialog';
import PhysicianNotes from '../StaffReviewPortal/v2/PhysicianNotes';
import CardDeletionRequest from '../StaffReviewPortal/v2/CardDeletionRequest';


function ImplementerReview() {
    const { draftId } = useParams();

    const { data: draft = {}, isSuccess: draftLoaded } = useGetDraftQuery(draftId);
    const { user } = useContext(UserContext);
    const [isShowingCheckedOutDialog, setIsShowingCheckedOutDialog] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [activeView, setActiveView] = useState('primaryView')
    const [updateReview] = useUpdateImplementerReviewMutation();
    const [updateDraft] = useUpdateDraftMutation();
    const [createCheckoutLog] = useCreateDraftCheckoutLogMutation();
    const [logUserAction] = useLogUserActionMutation();
    const [timeOfLastUpdate, setTimeOfLastUpdate] = useState(new Date());
    const [isShowingSubmitConfirmation, setIsShowingSubmitConfirmation] = useState(false);
    const [isShowingTimeoutDialog, setIsShowingTimeoutDialog] = useState(false);
    const [isDeletionDenied, setIsDeletionDenied] = useState(false);
    const navigate = useNavigate();
    

    useEffect(() => {
        if (draftLoaded) {
            if (draft.status === 'implemented') {
                setIsReadOnly(true);
                return;  // If the implementation is already complete, we don't need to check for checkout status since it will always be readonly. 
            }

            const checkoutStatus = draft.checkout;
            if (!checkoutStatus) {
                checkoutReview();
                return;
            }

            const currentTime = new Date();
            const timeSinceLastAction = differenceInMinutes(currentTime, new Date(draft.last_staff_review_action_time));
            const timeSinceCheckout = differenceInMinutes(currentTime, new Date(checkoutStatus.time_of_action));
                
            if (checkoutStatus.action === 'checked_out' && checkoutStatus.reviewer.id !== user.id && (timeSinceLastAction < 15 || timeSinceCheckout < 15)) {
                setIsShowingCheckedOutDialog(true);
                logAction('user_checkout_blocked', { draftId: draftId });
                setIsReadOnly(true);
            } else if (checkoutStatus.action === 'checked_in') {
                checkoutReview();
            } else if (checkoutStatus.action === 'checked_out' && timeSinceLastAction >= 15 && timeSinceCheckout >= 15) {
                checkoutReview();
            }
        }
    }, [draft, draftLoaded]);

    async function logAction(action, data) {
        await logUserAction({
            action: action,
            data: data
        });
    }

    const canSubmit = useMemo(() => {
        if (isReadOnly || draft.status !== 'applied') {
            return false;
        }

        let hasActionableReview = false;
        const allRecommendationsReviewed = Object.keys(draft.recommendations ?? {}).every((key) => {
            return draft.recommendations[key]?.every((rec) => {
                // if the rec doesn't have an implementer review yet or the implementer review is closed, then it's considered reviewed for the purposes of enabling the submit button
                if (!rec.implementer_review || rec.implementer_review.status === 'closed') {
                    return true;
                }

                hasActionableReview = true;
                return rec.implementer_review.status === 'staff_implemented' || (rec.implementer_review.status === 'rejected' && rec.implementer_review.notes);
            });
        });

        return allRecommendationsReviewed && (hasActionableReview || isDeletionDenied);
    }, [draft.items, draft.recommendations, isReadOnly, isDeletionDenied]);

    async function checkoutReview() {
        await createCheckoutLog({
            draftId: draftId,
            action: 'checked_out'
        });
    }

    async function handleUpdate(review, recommendation, newStatus, notes) {
        if (differenceInMinutes(new Date(), timeOfLastUpdate) >= 15) {
            setIsShowingTimeoutDialog(true);
            logAction('user_checkout_timed_out', { draftId: draftId });
            return;
        }
        
        await updateReview({
            review: review,
            draftId: draftId,
            recommendation: recommendation,
            newStatus: newStatus,
            notes: notes
        }).unwrap().then(() => {
            setTimeOfLastUpdate(new Date());
        });
    }

    async function handleSubmitReview() {
        await updateDraft({
            draftId: draftId,
            status: 'implemented'
        }).unwrap().then(() => {
            createCheckoutLog({
                draftId: draftId,
                action: 'checked_in',
                reason: 'review_submitted',
            });
        });
        setIsShowingSubmitConfirmation(false);
        navigate('/implementerInbox');
    }

    async function handleDeletionDecision(status) {
        if (status === 'denied') {
            logAction('card_deactivation_request_action_implementer', { draftId, status: 'denied' });
            setIsDeletionDenied(true);
            return;
        }
        await updateDraft({
            draftId: draftId,
            status: status
        }).unwrap().then(() => {
            logAction('card_deactivation_request_action_implementer', { draftId: draftId, status: status });
        });
    }

    return (
        <Stack px={4} py={2} spacing={2}>
            { draftLoaded &&
                <>
                    <ImplementerReviewCardHeader
                        draft={draft}
                        activeView={activeView}
                        setActiveView={setActiveView}
                        canSubmit={canSubmit}
                        setIsShowingSubmitConfirmation={setIsShowingSubmitConfirmation}
                    />
                    <PhysicianNotes 
                        notes={draft.physician_notes} 
                    />
                    <CardDeletionRequest
                        isMarkedForDeletion={draft.is_card_marked_for_deletion}
                        isReadOnly={isReadOnly}
                        updateDraft={(status) => handleDeletionDecision(status)}
                        variant='implementer'
                    />
                    { activeView === 'primaryView' && 
                        <StaffReviewChangesOnlyView 
                            variant='implementer'
                            review={draft} 
                            handleUpdate={handleUpdate}
                            isReadOnly={false}
                        />
                    }
                    { activeView === 'allCardItems' &&
                        <AllItemsView 
                            variant='implementer'
                            review={draft} 
                            handleUpdate={handleUpdate}
                            isReadOnly={false}
                        />
                    }
                    <SubmitReviewConfirmation 
                        open={isShowingSubmitConfirmation} 
                        handleClose={() => setIsShowingSubmitConfirmation(false)} 
                        handleSubmit={handleSubmitReview} 
                    />
                    <CheckedOutCardDialog 
                        draft={draft}
                        open={isShowingCheckedOutDialog} 
                        handleClose={() => setIsShowingCheckedOutDialog(false)} 
                        returnToActiveReviews={() => navigate('/implementerInbox')}
                    />
                    <CheckoutTimeoutDialog
                        draft={draft}
                        open={isShowingTimeoutDialog}
                        handleRefresh={() => window.location.reload()}
                    />
                </>
            }
        </Stack>
    );
}

export default ImplementerReview;

